/**
 * Shared Nostr context picker — composer + and project Context.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  X,
  User,
  Sparkles,
  Link2,
  Search,
  Loader2,
  MessagesSquare,
  Radio,
  BookOpen,
  BadgeCheck,
} from 'lucide-react';
import { useNostr } from '@nostrify/react';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useFollows } from '@/hooks/useFollows';
import {
  filterPeopleOnlyCandidates,
  isNetworkSearchCandidate,
  looksLikeNip05,
  resolveNip05,
  type MentionCandidate,
} from '@/lib/nostr/mentions';
import {
  makeEventChip,
  makeNetworkSearchChip,
  makeProfileChip,
  makeSelfChip,
  npubOf,
} from '@/lib/nostr/packs';
import {
  fetchNostrEntity,
  parseNostrRef,
} from '@/lib/nostr/fetchEntity';
import { getProfileCached } from '@/lib/profileCache';
import type { NostrContextChip, NostrContextPack } from '@/lib/workspace/types';
import { cn } from '@/lib/utils';

export type NostrPickerMode = 'composer' | 'project';

export interface NostrPickerProps {
  open: boolean;
  onClose: () => void;
  mode: NostrPickerMode;
  onAddChips: (chips: NostrContextChip[]) => void;
  /** When mode=project, parent turns chips into context items */
}

type Tab = 'quick' | 'paste' | 'people';

export function NostrPicker({ open, onClose, mode, onAddChips }: NostrPickerProps) {
  const { nostr } = useNostr();
  const { user, metadata } = useCurrentUser();
  const { candidates, isLoading: followsLoading } = useFollows();

  const [tab, setTab] = useState<Tab>('quick');
  const [paste, setPaste] = useState('');
  const [pastePack, setPastePack] = useState<'single' | 'thread' | 'article'>('single');
  const [preview, setPreview] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [peopleQ, setPeopleQ] = useState('');
  const [nip05Busy, setNip05Busy] = useState(false);
  const [nip05Hit, setNip05Hit] = useState<MentionCandidate | null>(null);

  const selfName =
    (typeof metadata?.display_name === 'string' && metadata.display_name) ||
    (typeof metadata?.name === 'string' && metadata.name) ||
    undefined;

  useEffect(() => {
    if (!open) {
      setTab('quick');
      setPaste('');
      setPreview(null);
      setPreviewError(null);
      setPeopleQ('');
      setNip05Hit(null);
    }
  }, [open]);

  const queryNostr = useCallback(
    async (
      filters: Parameters<typeof nostr.query>[0],
      opts?: { signal?: AbortSignal },
    ) => nostr.query(filters, opts),
    [nostr],
  );

  // Live preview on paste
  useEffect(() => {
    if (!open || tab !== 'paste') return;
    const ref = paste.trim();
    if (!ref || ref.length < 8) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    const compact = ref.replace(/\s+/g, '');
    const parsed = parseNostrRef(compact);
    if (!parsed && !looksLikeNip05(ref)) {
      setPreview(null);
      setPreviewError('Paste a note, nevent, npub, nprofile, naddr, or name@domain');
      return;
    }

    let cancelled = false;
    const t = window.setTimeout(() => {
      void (async () => {
        setPreviewLoading(true);
        setPreviewError(null);
        try {
          if (looksLikeNip05(ref) && !parseNostrRef(compact)) {
            const hit = await resolveNip05(ref, {
              corsProxyTemplate: 'https://proxy.shakespeare.diy/?url={href}',
            });
            if (cancelled) return;
            if (!hit) {
              setPreview(null);
              setPreviewError('NIP-05 not found');
              return;
            }
            const r = await fetchNostrEntity(npubOf(hit.pubkey), queryNostr);
            if (cancelled) return;
            if (r.ok) {
              setPreview(r.text.slice(0, 800));
              setPreviewError(null);
            } else {
              setPreview(null);
              setPreviewError(r.error);
            }
            return;
          }
          const r = await fetchNostrEntity(compact, queryNostr, {
            timeoutMs: parsed?.kind === 'naddr' ? 14_000 : 10_000,
          });
          if (cancelled) return;
          if (r.ok) {
            setPreview(r.text.slice(0, 1200));
            setPreviewError(null);
          } else {
            setPreview(null);
            setPreviewError(r.error);
          }
        } catch (e) {
          if (!cancelled) {
            setPreview(null);
            setPreviewError(e instanceof Error ? e.message : 'Preview failed');
          }
        } finally {
          if (!cancelled) setPreviewLoading(false);
        }
      })();
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [paste, open, tab, queryNostr]);

  // People tab must never list the special @nostr network token as a "person"
  // (that used filterMentionCandidates which injects it when the query is empty
  // or starts with "nostr" — clicking + notes then tried to fetch a fake pubkey).
  const people = useMemo(
    () => filterPeopleOnlyCandidates(candidates, peopleQ, 16),
    [candidates, peopleQ],
  );

  const addNetworkSearch = () => {
    onAddChips([makeNetworkSearchChip()]);
    onClose();
  };

  // NIP-05 search when query looks like email
  useEffect(() => {
    if (!open || tab !== 'people') return;
    const q = peopleQ.trim();
    if (!looksLikeNip05(q)) {
      setNip05Hit(null);
      return;
    }
    let cancelled = false;
    setNip05Busy(true);
    void resolveNip05(q, {
      corsProxyTemplate: 'https://proxy.shakespeare.diy/?url={href}',
    }).then(async (hit) => {
      if (cancelled) return;
      if (!hit) {
        setNip05Hit(null);
        setNip05Busy(false);
        return;
      }
      const r = await fetchNostrEntity(npubOf(hit.pubkey), queryNostr);
      if (cancelled) return;
      let displayName = hit.pubkey.slice(0, 12);
      let picture: string | undefined;
      let nip05 = q.replace(/^@/, '');
      if (r.ok) {
        const nameMatch = r.text.match(/^name: (.+)$/m);
        if (nameMatch) displayName = nameMatch[1]!;
        const picMatch = r.text.match(/^picture: (.+)$/m);
        if (picMatch) picture = picMatch[1];
        const nipMatch = r.text.match(/^nip05: (.+)$/m);
        if (nipMatch) nip05 = nipMatch[1]!;
      }
      setNip05Hit({
        pubkey: hit.pubkey,
        npub: npubOf(hit.pubkey),
        displayName,
        nip05,
        picture,
        source: 'nip05',
      });
      setNip05Busy(false);
    });
    return () => {
      cancelled = true;
    };
  }, [peopleQ, open, tab, queryNostr]);

  const addSelf = (pack: 'my-profile' | 'my-recent') => {
    if (!user?.pubkey) return;
    const picture =
      typeof metadata?.picture === 'string' ? metadata.picture : undefined;
    const nip05 = typeof metadata?.nip05 === 'string' ? metadata.nip05 : undefined;
    onAddChips([makeSelfChip(pack, user.pubkey, selfName, { picture, nip05 })]);
    onClose();
  };

  const addPerson = (c: MentionCandidate, pack: 'profile-brief' | 'profile-recent') => {
    // Guard: network-search token is not a person (legacy / typeahead bleed)
    if (isNetworkSearchCandidate(c)) {
      addNetworkSearch();
      return;
    }
    // Prefer picture from candidate; profile cache fills gaps
    let picture = c.picture;
    if (!picture) {
      const cached = getProfileCached(c.pubkey);
      if (typeof cached?.metadata?.picture === 'string') {
        picture = cached.metadata.picture;
      }
    }
    onAddChips([
      makeProfileChip(c.pubkey, {
        displayName: c.displayName,
        nip05: c.nip05,
        // Rows from a NIP-05 search were forward-resolved to this pubkey
        nip05Verified: c.source === 'nip05' ? true : undefined,
        picture,
        pack,
      }),
    ]);
    onClose();
  };

  const addPaste = async () => {
    const ref = paste.trim();
    if (!ref) return;

    if (looksLikeNip05(ref) && !parseNostrRef(ref)) {
      setPreviewLoading(true);
      const hit = await resolveNip05(ref, {
        corsProxyTemplate: 'https://proxy.shakespeare.diy/?url={href}',
      });
      setPreviewLoading(false);
      if (!hit) {
        setPreviewError('Could not resolve NIP-05');
        return;
      }
      onAddChips([
        makeProfileChip(hit.pubkey, {
          displayName: ref.replace(/^@/, ''),
          nip05: ref.replace(/^@/, ''),
          nip05Verified: true, // forward-resolved just now
          pack: 'profile-recent',
        }),
      ]);
      onClose();
      return;
    }

    const compactRef = ref.replace(/\s+/g, '');
    const parsed = parseNostrRef(compactRef);
    if (!parsed) {
      setPreviewError('Unrecognized reference');
      return;
    }

    if (parsed.kind === 'npub' || parsed.kind === 'nprofile' || parsed.kind === 'hex-pubkey') {
      const pubkey =
        parsed.kind === 'hex-pubkey' ? parsed.pubkey : parsed.pubkey;
      let displayName: string | undefined;
      let nip05: string | undefined;
      let picture: string | undefined;
      try {
        setPreviewLoading(true);
        const r = await fetchNostrEntity(npubOf(pubkey), queryNostr);
        if (r.ok) {
          displayName = r.meta?.displayName;
          nip05 = r.meta?.nip05;
          picture = r.meta?.picture;
        }
      } catch {
        /* ignore */
      } finally {
        setPreviewLoading(false);
      }
      onAddChips([
        makeProfileChip(pubkey, {
          pack: 'profile-recent',
          displayName,
          nip05,
          picture,
        }),
      ]);
      onClose();
      return;
    }

    // Auto-pick article pack for NIP-23 naddrs even if user left "Event" selected
    let pack: NostrContextPack =
      pastePack === 'thread' ? 'thread' : pastePack === 'article' ? 'article' : 'single';
    if (parsed.kind === 'naddr' && parsed.eventKind === 30023 && pack === 'single') {
      pack = 'article';
    }

    let image: string | undefined;
    let picture: string | undefined;
    let title =
      parsed.kind === 'naddr'
        ? parsed.eventKind === 30023
          ? `Article · ${parsed.identifier}`
          : `naddr k${parsed.eventKind} · ${parsed.identifier}`
        : preview?.split('\n')[0]?.replace(/^#+\s*/, '').slice(0, 48) || 'Event';
    let pubkey: string | undefined =
      parsed.kind === 'naddr' ? parsed.pubkey : undefined;
    let eventId: string | undefined;

    try {
      setPreviewLoading(true);
      const r = await fetchNostrEntity(compactRef, queryNostr, {
        timeoutMs: parsed.kind === 'naddr' ? 14_000 : 10_000,
        includeAuthor: true,
      });
      if (r.ok) {
        if (r.meta?.title) title = r.meta.title;
        image = r.meta?.image;
        picture = r.meta?.picture;
        pubkey = r.pubkey || pubkey;
        eventId = r.eventId;
        if (r.meta?.eventKind === 30023) pack = 'article';
      }
    } catch {
      /* use defaults */
    } finally {
      setPreviewLoading(false);
    }

    onAddChips([
      makeEventChip(compactRef, {
        title,
        pack,
        snippet: preview?.slice(0, 200),
        image,
        picture,
        pubkey,
        eventId,
      }),
    ]);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add Nostr context"
        className="relative w-full sm:max-w-md max-h-[min(88dvh,640px)] flex flex-col rounded-t-2xl sm:rounded-2xl border border-[#2a2a2a] bg-[#0f0f0f] shadow-2xl"
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[#1f1f1f]">
          <div>
            <h2 className="text-[15px] font-medium text-[#e8e6e3]">Add Nostr context</h2>
            <p className="text-[11px] text-[#5a5a5a] mt-0.5">
              {mode === 'project'
                ? 'Pinned to this project’s context pack'
                : 'Attached to this message — fetched on send'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex gap-1 px-3 pt-2 border-b border-[#1f1f1f]">
          {(
            [
              { id: 'quick' as const, label: 'Quick' },
              { id: 'people' as const, label: 'People' },
              { id: 'paste' as const, label: 'Paste' },
            ] as const
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'px-3 py-2 text-[12px] rounded-t-lg transition-colors',
                tab === t.id
                  ? 'text-[#e8e6e3] bg-[#161616] border border-b-0 border-[#2a2a2a]'
                  : 'text-[#5a5a5a] hover:text-[#8a8884]',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-2">
          {tab === 'quick' && (
            <>
              {user?.pubkey ? (
                <>
                  <button
                    type="button"
                    onClick={addNetworkSearch}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl border border-amber-900/40 bg-[#141414] hover:border-amber-800/50 hover:bg-[#161616] transition-colors text-left"
                  >
                    <div className="w-9 h-9 rounded-full bg-amber-950/40 flex items-center justify-center text-amber-200/80">
                      <Radio className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] text-[#e8e6e3] font-medium">
                        Search my follows (@nostr)
                      </div>
                      <div className="text-[11px] text-[#5a5a5a]">
                        Recent notes from people you follow — keywords from your message
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => addSelf('my-profile')}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl border border-[#2a2a2a] bg-[#141414] hover:border-[#3a3a3a] hover:bg-[#161616] transition-colors text-left"
                  >
                    <div className="w-9 h-9 rounded-full bg-[#1a1a1a] flex items-center justify-center text-[#8a8884]">
                      <User className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] text-[#e8e6e3] font-medium">My profile</div>
                      <div className="text-[11px] text-[#5a5a5a] truncate">
                        {selfName || 'kind-0 metadata for the logged-in account'}
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => addSelf('my-recent')}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl border border-[#2a2a2a] bg-[#141414] hover:border-[#3a3a3a] hover:bg-[#161616] transition-colors text-left"
                  >
                    <div className="w-9 h-9 rounded-full bg-[#1a1a1a] flex items-center justify-center text-[#8a8884]">
                      <Sparkles className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] text-[#e8e6e3] font-medium">
                        My recent notes
                      </div>
                      <div className="text-[11px] text-[#5a5a5a]">
                        Profile + last public kind-1 notes
                      </div>
                    </div>
                  </button>
                </>
              ) : (
                <p className="text-[12px] text-[#5a5a5a] px-1 py-2">
                  Log in to attach your own profile and notes in one click.
                </p>
              )}

              <p className="text-[10px] text-[#3a3a3a] px-1 pt-2 leading-relaxed">
                Tip: type <span className="text-[#5a5a5a]">@</span> in the composer to mention
                people you follow — same packs, Ditto-style.
              </p>
              <p className="text-[10px] text-[#3a3a3a] px-1 leading-relaxed">
                Fetched public content is sent to your AI provider with the prompt.
              </p>
            </>
          )}

          {tab === 'people' && (
            <>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#3a3a3a]" />
                <input
                  value={peopleQ}
                  onChange={(e) => setPeopleQ(e.target.value)}
                  placeholder="Search follows, npub, or name@domain"
                  autoFocus
                  className="w-full bg-[#141414] border border-[#2a2a2a] rounded-xl pl-8 pr-3 py-2.5 text-[13px] text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none focus:border-[#3a3a3a]"
                />
              </div>

              {/* Same action as Quick → Search my follows — not a person row */}
              {user?.pubkey && !peopleQ.trim() && (
                <button
                  type="button"
                  onClick={addNetworkSearch}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-amber-900/35 bg-[#141414] hover:border-amber-800/45 hover:bg-[#161616] transition-colors text-left"
                >
                  <div className="w-9 h-9 rounded-full bg-amber-950/40 flex items-center justify-center text-amber-200/80 shrink-0">
                    <Radio className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] text-[#e8e6e3] font-medium">
                      Search my follows
                    </div>
                    <div className="text-[11px] text-[#5a5a5a]">
                      @nostr · keywords from your message · not a person
                    </div>
                  </div>
                </button>
              )}

              {followsLoading && people.length === 0 && (
                <div className="flex items-center gap-2 text-[12px] text-[#5a5a5a] py-4 justify-center">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Loading follows…
                </div>
              )}

              {nip05Busy && (
                <div className="flex items-center gap-2 text-[12px] text-[#5a5a5a] py-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Resolving NIP-05…
                </div>
              )}

              {nip05Hit && !isNetworkSearchCandidate(nip05Hit) && (
                <PersonRow
                  c={nip05Hit}
                  verified
                  onBrief={() => addPerson(nip05Hit, 'profile-brief')}
                  onRecent={() => addPerson(nip05Hit, 'profile-recent')}
                />
              )}

              {people.map((c) => (
                <PersonRow
                  key={c.pubkey + c.source}
                  c={c}
                  onBrief={() => addPerson(c, 'profile-brief')}
                  onRecent={() => addPerson(c, 'profile-recent')}
                />
              ))}

              {!followsLoading && people.length === 0 && !nip05Hit && peopleQ.trim() && (
                <p className="text-[12px] text-[#5a5a5a] text-center py-6 leading-relaxed px-4">
                  No matches. Follow people on Nostr, paste an npub, or enter a NIP-05 address.
                </p>
              )}

              {!followsLoading && people.length === 0 && !nip05Hit && !peopleQ.trim() && (
                <p className="text-[12px] text-[#5a5a5a] text-center py-4 leading-relaxed px-4">
                  Your follows will show here. Use Search my follows above for keyword search
                  across them, or type a name / npub / NIP-05.
                </p>
              )}
            </>
          )}

          {tab === 'paste' && (
            <>
              <div className="flex gap-1 mb-1">
                {(
                  [
                    { id: 'single' as const, label: 'Event', icon: Link2 },
                    { id: 'thread' as const, label: 'Thread', icon: MessagesSquare },
                    { id: 'article' as const, label: 'Article', icon: BookOpen },
                  ] as const
                ).map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPastePack(p.id)}
                    className={cn(
                      'flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] transition-colors',
                      pastePack === p.id
                        ? 'bg-[#e8e6e3] text-[#0a0a0a]'
                        : 'text-[#5a5a5a] hover:text-[#e8e6e3] bg-[#141414]',
                    )}
                  >
                    <p.icon className="w-3 h-3" />
                    {p.label}
                  </button>
                ))}
              </div>
              <textarea
                value={paste}
                onChange={(e) => setPaste(e.target.value)}
                placeholder="note1… / nevent1… / npub1… / naddr1… / name@domain"
                rows={3}
                autoFocus
                className="w-full bg-[#141414] border border-[#2a2a2a] rounded-xl px-3 py-2.5 text-[13px] text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none focus:border-[#3a3a3a] font-mono resize-y"
              />
              {previewLoading && (
                <div className="flex items-center gap-2 text-[11px] text-[#5a5a5a]">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Fetching preview…
                </div>
              )}
              {previewError && (
                <p className="text-[11px] text-amber-500/90">{previewError}</p>
              )}
              {preview && !previewError && (
                <pre className="text-[10px] text-[#5a5a5a] bg-[#0a0a0a] border border-[#1f1f1f] rounded-xl p-2.5 max-h-36 overflow-y-auto whitespace-pre-wrap font-mono leading-relaxed">
                  {preview}
                </pre>
              )}
              <button
                type="button"
                disabled={!paste.trim() || previewLoading}
                onClick={() => void addPaste()}
                className="w-full py-2.5 rounded-xl bg-[#e8e6e3] text-[#0a0a0a] text-[13px] font-medium disabled:opacity-30"
              >
                Add to {mode === 'project' ? 'project' : 'message'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PersonRow({
  c,
  verified,
  onBrief,
  onRecent,
}: {
  c: MentionCandidate;
  /** NIP-05 was forward-resolved to this pubkey (verified by construction) */
  verified?: boolean;
  onBrief: () => void;
  onRecent: () => void;
}) {
  return (
    <div className="flex items-center gap-2 px-2 py-2 rounded-xl border border-[#1f1f1f] bg-[#121212]">
      {c.picture ? (
        <img
          src={c.picture}
          alt=""
          className="w-9 h-9 rounded-full object-cover shrink-0 bg-[#1a1a1a]"
        />
      ) : (
        <div className="w-9 h-9 rounded-full bg-[#1a1a1a] flex items-center justify-center text-[#5a5a5a] shrink-0">
          <User className="w-4 h-4" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-[#e8e6e3] truncate font-medium">
          {c.source === 'self' ? `${c.displayName} (you)` : c.displayName}
        </div>
        <div className="text-[10px] text-[#5a5a5a] truncate flex items-center gap-1">
          <span className="truncate">{c.nip05 || c.npub.slice(0, 18) + '…'}</span>
          {verified && c.nip05 && (
            <BadgeCheck
              className="w-3 h-3 shrink-0 text-sky-400/80"
              aria-label="NIP-05 verified"
            />
          )}
        </div>
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <button
          type="button"
          onClick={onRecent}
          className="px-2 py-1 text-[10px] rounded-md bg-[#e8e6e3] text-[#0a0a0a] font-medium"
          title="Profile + recent notes"
        >
          + notes
        </button>
        <button
          type="button"
          onClick={onBrief}
          className="px-2 py-1 text-[10px] rounded-md border border-[#2a2a2a] text-[#8a8884] hover:text-[#e8e6e3]"
          title="Profile only"
        >
          profile
        </button>
      </div>
    </div>
  );
}
